import { sdkTypes } from '@vansite/ts-sharetribe-flex-sdk';
import { Box } from '@mui/material';
import React from 'react';
import { useIntl } from 'react-intl';

import config from '../../../config';
import { createMixins } from '../../../styles/mui/base/create-mixins';
import { createTokens } from '../../../styles/mui/base/create-tokens';
import { formatMoney } from '../../../util/currency';
import { ensureListing } from '../../../util/data';
import useAppSelector from '../../../util/hooks/useAppSelector';

const { Money } = sdkTypes;

const tokens = createTokens();
const mixins = createMixins(tokens);

// Internal selector hooks so the root :hover can target its children (DOM unchanged).
const PRICE_LABEL_CLASS = 'SearchMapPriceLabel-priceLabel';
const CARET_CLASS = 'SearchMapPriceLabel-caret';

const rootSx = {
  position: 'relative',
  width: 'auto',
  height: 'auto',
  border: 0,
  padding: 0,

  '&:hover': {
    [`& .${PRICE_LABEL_CLASS}, & .${CARET_CLASS}`]: {
      cursor: 'pointer',
      // Same as active
      backgroundColor: tokens.marketplaceColor,
      borderColor: tokens.marketplaceColor,
      color: tokens.matterColorLight,
      boxShadow: tokens.boxShadowPopup,
    },
  },
};

const priceLabelSx = (isActive, isBookmarked) => ({
  // Since caret is absolutely positioned, label must have relative to be
  // included to the same rendering layer
  position: 'relative',

  // Font
  ...mixins.marketplaceH5FontStyles,
  fontWeight: tokens.fontWeightSemiBold,
  color: tokens.matterColor,
  letterSpacing: '0.1px',

  backgroundColor: tokens.matterColorLight,

  // Borders
  borderStyle: 'solid',
  borderColor: tokens.matterColorNegative,
  borderWidth: '1px',
  borderRadius: '50px',
  boxShadow: tokens.boxShadowPopupLight,

  // Dimensions
  padding: '6px 10px',
  marginTop: 0,
  marginBottom: 0,
  // Property-specific transition (see original CSS comment).
  transition:
    'background-color 0.1s ease-in-out, border-color 0.1s ease-in-out, color 0.1s ease-in-out, box-shadow 0.1s ease-in-out',

  // Overwrite dimensions from font styles
  '@media (min-width:900px)': {
    marginTop: 0,
    marginBottom: 0,
  },

  ...(isBookmarked && {
    borderColor: tokens.marketplaceColorLight,
    backgroundColor: tokens.marketplaceColorLight,
  }),

  ...(isActive && {
    // Same as hover
    backgroundColor: tokens.marketplaceColor,
    borderColor: tokens.marketplaceColor,
    color: tokens.matterColorLight,
    boxShadow: tokens.boxShadowPopup,
  }),
});

const caretShadowSx = {
  // Caret / arrow dimensions and position
  width: '6px',
  height: '6px',
  position: 'absolute',
  bottom: '-3px',
  left: '50%',
  marginLeft: '-3px',
  transform: 'rotate(45deg)',

  // Caret should have same box-shadow as label
  boxShadow: tokens.boxShadowPopupLight,
};

const caretSx = (isActive, isBookmarked) => ({
  // Caret / arrow dimensions and position
  width: '6px',
  height: '6px',
  position: 'absolute',
  bottom: '-3px',
  left: '50%',
  marginLeft: '-3px',
  transform: 'rotate(45deg)',

  // Limit transition to props that actually animate between states.
  transition: 'background-color 0.1s ease-in-out, border-color 0.1s ease-in-out',

  // Caret should have same bg-color and border as label
  backgroundColor: tokens.matterColorLight,
  borderRightStyle: 'solid',
  borderRightColor: tokens.matterColorNegative,
  borderRightWidth: '1px',
  borderBottomStyle: 'solid',
  borderBottomColor: tokens.matterColorNegative,
  borderBottomWidth: '1px',

  ...(isBookmarked && {
    backgroundColor: tokens.marketplaceColorLight,
    borderColor: tokens.marketplaceColorLight,
  }),

  ...(isActive && {
    // Same as hover
    backgroundColor: tokens.marketplaceColor,
    borderColor: tokens.marketplaceColor,
  }),
});

const SearchMapPriceLabel = ({ className = null, rootClassName = null, listing, onListingClicked, isActive }) => {
  const intl = useIntl();
  const currentListing = ensureListing(listing);
  const { price } = currentListing.attributes;
  const currentBookmarks = useAppSelector(state => state.bookmarks.currentBookmarks);

  const priceWithCustomerCommission = new Money(
    Math.round(price.amount + price.amount * (config.customerCommissionPercentage / 100)),
    price.currency,
  );
  // Create formatted price if currency is known or alternatively show just the unknown currency.
  const formattedPrice =
    price && price.currency === config.currency
      ? formatMoney(intl, priceWithCustomerCommission, 0)
      : price.currency;

  const isBookmarked = currentBookmarks.some(bookmark => bookmark === currentListing.id.uuid);

  return (
    <Box
      className={[rootClassName, className].filter(Boolean).join(' ') || undefined}
      component="button"
      onClick={() => onListingClicked(currentListing)}
      sx={rootClassName ? undefined : rootSx}
    >
      <Box sx={caretShadowSx} />
      <Box className={PRICE_LABEL_CLASS} sx={priceLabelSx(isActive, isBookmarked)}>
        {formattedPrice}
      </Box>
      <Box className={CARET_CLASS} sx={caretSx(isActive, isBookmarked)} />
    </Box>
  );
};

export default SearchMapPriceLabel;
